import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import './OrderBoostDetails.scss'

import { HorizontalLoader, ModalWrapper } from 'Components'
import { DateTimeFormatter } from 'Helpers/DateTimeFormatter'
import { OrderXModel } from 'BoostXpressAdmin/Model/orderXmodel'
import { orderBXService } from 'BoostXpressAdmin/Services/orderBXService'
import { CurrencyFormatter, handleSuccess } from 'Helpers'
import { handleError } from 'Helpers/alert/alerts'
import { ToastContainer } from 'react-toastify'

const OrderBoostDetails = () => {
  const { orderId } = useParams()

  const [order, setOrder] = useState<OrderXModel | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [cancelSubmitting, setCancelSubmitting] = useState(false)

  const fetchOrder = async () => {
    setIsLoading(true)

    await orderBXService.getById(orderId || '').then(
      (res) => {
        setOrder(res.data.data)

        setIsLoading(false)
      },
      (error) => {
        handleError(error)
        setIsLoading(false)
      },
    )
  }

  useEffect(() => {
    fetchOrder()
  }, [orderId])

  const handleCancelOrder = async () => {
    if (!orderId || cancelSubmitting) return
    setCancelSubmitting(true)
    await orderBXService.cancel(orderId).then(
      async () => {
        await fetchOrder()
        setShowCancelModal(false)
        handleSuccess('Order cancelled successfully.')
      },
      (error) => {
        handleError(error)
        setCancelSubmitting(false)
      },
    )
  }

  if (isLoading) {
    return <HorizontalLoader />
  }

  if (!order) {
    return <div>Order not found.</div>
  }

  return (
    <div className='bx-order-details'>
      <div className='bx-order-details__header'>
        <h2>Order Details</h2>
        {order.status !== 'CANCELLED' ? (
          <button
            type='button'
            className='bx-order-details__cancel-btn'
            onClick={() => setShowCancelModal(true)}
            disabled={cancelSubmitting}
          >
            Cancel Order
          </button>
        ) : null}
      </div>

      <div className='bx-order-details__card'>
        <div className='bx-order-details__grid'>
          <div>
            <label>Reference Number</label>
            <p>{order.referenceNumber}</p>
          </div>

          <div>
            <label>Product</label>
            <p>{order.product}</p>
          </div>

          <div>
            <label>Status</label>
            <p className={order.status === 'CANCELLED' ? 'text-danger' : 'text-success'}>
              {order.status}
            </p>
          </div>

          <div>
            <label>Payment Status</label>
            <p>{order.paymentStatus}</p>
          </div>

          <div>
            <label>Quantity</label>
            <p>
              {order.quantity} {order.unitOfMeasure}
            </p>
          </div>

          <div>
            <label>Origin Quantity</label>
            <p>
              {order.originQuantity} {order.unitOfMeasure}
            </p>
          </div>

          <div>
            <label>Dispensed Quantity</label>
            <p>{order.dispensedQuantity || '-'}</p>
          </div>

          <div>
            <label>Pump Price</label>
            <p>
              {order.currency} {CurrencyFormatter(order.price)}
            </p>
          </div>

          <div>
            <label>Origin Price</label>
            <p>
              {order.currency} {CurrencyFormatter(order.originPrice)}
            </p>
          </div>

          <div>
            <label>Amount</label>
            <p>
              {order.currency} {CurrencyFormatter(order.costMajor)}
            </p>
          </div>

          <div>
            <label>Payment Method</label>
            <p>{order.paymentMethod}</p>
          </div>

          <div>
            <label>Fulfillment Code</label>
            <p>{order.fulfillmentCode}</p>
          </div>

          <div>
            <label>Verification Code</label>
            <p>{order.verificationCode}</p>
          </div>

          <div>
            <label>Is Reconcilable</label>
            <p>{order.isReconcilable ? 'Yes' : 'No'}</p>
          </div>

          <div>
            <label>Station ID</label>
            <p>{order.stationId}</p>
          </div>

          <div>
            <label>Origin Station ID</label>
            <p>{order.originStationId}</p>
          </div>

          <div>
            <label>Created At</label>
            <p>{DateTimeFormatter(order.createdAt)}</p>
          </div>

          <div>
            <label>Completed At</label>
            <p>{order.completedAt ? DateTimeFormatter(order.completedAt) : '-'}</p>
          </div>

          <div>
            <label>Customer Name</label>
            <p>
              {order.user.firstName} {order.user.lastName}
            </p>
          </div>

          <div>
            <label>Customer ID</label>
            <p>{order.user.resourceId}</p>
          </div>

          <div>
            <label>Customer Email</label>
            <p>{order.user.email}</p>
          </div>

          <div>
            <label>Customer Phone</label>
            <p>{order.user.phone}</p>
          </div>

          <div>
            <label>Email Verified At</label>
            <p>{order.user.emailVerifiedAt ? DateTimeFormatter(order.user.emailVerifiedAt) : '-'}</p>
          </div>

          <div>
            <label>Phone Verified At</label>
            <p>{order.user.phoneVerifiedAt ? DateTimeFormatter(order.user.phoneVerifiedAt) : '-'}</p>
          </div>

          <div>
            <label>Disabled At</label>
            <p>{order.user.disabledAt ? DateTimeFormatter(order.user.disabledAt) : '-'}</p>
          </div>

          <div>
            <label>Is Disabled</label>
            <p>{order.user.isDisabled ? 'Yes' : 'No'}</p>
          </div>

          <div>
            <label>Account Type</label>
            <p>{order.user.accountType}</p>
          </div>
        </div>
 
        <div className='bx-order-details__section border-top pt-4'>
          <h5>Station Details</h5>
          <div className='bx-order-details__grid'>
            <div>
              <label>Business Name</label>
              <p>{order.station.businessName}</p>
            </div>

            <div>
              <label>Branch Name</label>
              <p>{order.station.branchName}</p>
            </div>

            <div>
              <label>Address</label>
              <p>{order.station.address}</p>
            </div>

            <div>
              <label>City</label>
              <p>{order.station.city}</p>
            </div>

            <div>
              <label>State</label>
              <p>{order.station.state}</p>
            </div>

            <div>
              <label>Account Manager ID</label>
              <p>{order.station.accountManagerAccountId}</p>
            </div>

            <div>
              <label>Latitude</label>
              <p>{order.station.latitude}</p>
            </div>

            <div>
              <label>Longitude</label>
              <p>{order.station.longitude}</p>
            </div>

            <div>
              <label>Is Public</label>
              <p>{order.station.isPublic ? 'Yes' : 'No'}</p>
            </div>

            <div>
              <label>Opening Time</label>
              <p>{order.station.openingTimeHour}</p>
            </div>

            <div>
              <label>Closing Time</label>
              <p>{order.station.closingTimeHour}</p>
            </div>
          </div>
        </div>

        {/* Station Products Section */}
        {order.station.products && order.station.products.length > 0 && (
          <div className='bx-order-details__section  border-top pt-4'>
            <h5>Station Products</h5>
            <div className='bx-order-details__grid'>
              {order.station.products.map((product, index) => (
                <div key={index}>
                  <label>{product.product}</label>
                  <p>
                    {product.priceCurrency} {CurrencyFormatter(product.price)} / {product.unitOfMeasure}
                  </p>
                  <p>Available: {product.isAvailable ? 'Yes' : 'No'}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Station Tanks Section */}
        {order.station.tanks && order.station.tanks.length > 0 && (
          <div className='bx-order-details__section border-top pt-4'>
            <h5>Station Tanks</h5>
            <div className='bx-order-details__grid'>
              {order.station.tanks.map((tank, index) => (
                <div key={index}>
                  <label>{tank.product}</label>
                  <p>Quantity: {tank.quantity}</p>
                </div>
              ))}
            </div>
          </div>
        )}
 

        {order.virtualAccount && (
          <div className='bx-order-details__section border-top pt-4'>
            <h5>Virtual Account</h5>

            <div className='bx-order-details__grid'>
              <div>
                <label>Provider</label>
                <p>{order.virtualAccount.accountProvider}</p>
              </div>

              <div>
                <label>Bank Name</label>
                <p>{order.virtualAccount.bankName}</p>
              </div>

              <div>
                <label>Account Number</label>
                <p>{order.virtualAccount.bankAccountNumber}</p>
              </div>

              <div>
                <label>Account Name</label>
                <p>{order.virtualAccount.bankAccountName}</p>
              </div>

              <div>
                <label>Expires At</label>
                <p>{DateTimeFormatter(order.virtualAccount.expiresAt)}</p>
              </div>
            </div>
          </div>
        )}

        {order.fuelPump && (
          <div className='bx-order-details__section border-top pt-4'>
            <h5>Fuel Pump</h5>

            <div className='bx-order-details__grid'>
              <div>
                <label>Pump ID</label>
                <p>{order.fuelPump.id}</p>
              </div>

              <div>
                <label>Pump Number</label>
                <p>{order.fuelPump.pumpNumber}</p>
              </div>

              <div>
                <label>Fuel Fact Pump Side ID</label>
                <p>{order.fuelPump.fuelFactPumpSideId}</p>
              </div>
            </div>
          </div>
        )}
      </div>
      <ModalWrapper
        showModal={showCancelModal}
        closeModal={() => {
          if (!cancelSubmitting) setShowCancelModal(false)
        }}
        title='Cancel Order'
        hideHeader={false}
      >
        <div className='bx-order-details__modal'>
          <p className='bx-order-details__modal-copy'>
            Are you sure you want to cancel order `{order.referenceNumber}`?
          </p>
          <div className='bx-order-details__modal-actions d-flex justify-content-start'>
            <button
              type='button'
              className='btn btn-default'
              onClick={handleCancelOrder}
              disabled={cancelSubmitting}
            >
              {cancelSubmitting ? 'Cancelling...' : 'Confirm Cancel'}
            </button>
          </div>
        </div>
      </ModalWrapper>
      <ToastContainer />
    </div>
  )
}

export default OrderBoostDetails
